import React from 'react'
import { Checkbox, Modal, Space } from 'antd'
import { Button } from '@surecompanies/core_components'
import {
  ModalSubtitle,
  SyncBulkBar,
  SyncBulkCount,
  SyncBulkLabel,
  SyncEmptyState,
  SyncField,
  SyncFieldHint,
  SyncFieldLabel,
  SyncFooterSummary,
  SyncModalFooter,
  SyncModalScroll,
  SyncStepGroup,
  SyncStepGroupTitle,
  SyncSubmitError,
} from './ClientBillingProfileManagement.styles'
import { HubspotSyncEmailList } from './HubspotSyncEmailList'
import type { useHubspotSyncChanges } from './useHubspotSyncChanges'

interface HubspotSyncChangesModalProps {
  open: boolean
  state: ReturnType<typeof useHubspotSyncChanges>
  onClose: () => void
}

/**
 * Existing companies HubSpot has something to add to. Untick a company to leave
 * it as it is; recipients HubSpot dropped are decided in Needs review instead.
 */
const HubspotSyncChangesModal = ({
  open,
  state,
  onClose,
}: HubspotSyncChangesModalProps) => (
  <Modal
    open={open}
    onCancel={onClose}
    title="Recipient updates"
    footer={null}
    destroyOnClose
    width={640}
    style={{ maxWidth: 'calc(100vw - 32px)' }}
  >
    <ModalSubtitle>
      {state.canGrant
        ? 'HubSpot has new recipients for these companies. Ticked addresses also get access to the funding portal, and anyone without an account is emailed an invitation.'
        : 'HubSpot has new recipients for these companies.'}
    </ModalSubtitle>

    {state.rows.length === 0 ? (
      <SyncEmptyState>No recipient updates.</SyncEmptyState>
    ) : (
      <>
        <SyncBulkBar>
          <Checkbox
            checked={
              state.totalCount > 0 && state.includedCount === state.totalCount
            }
            indeterminate={
              state.includedCount > 0 && state.includedCount < state.totalCount
            }
            onChange={(event) => state.setAllIncluded(event.target.checked)}
          >
            <SyncBulkLabel>
              <SyncBulkCount>{state.includedCount}</SyncBulkCount> of{' '}
              {state.totalCount}{' '}
              {state.totalCount === 1 ? 'company' : 'companies'}
            </SyncBulkLabel>
          </Checkbox>

          {state.canGrant ? (
            <Checkbox
              checked={
                state.grantableCount > 0 &&
                state.grantCount === state.grantableCount
              }
              indeterminate={
                state.grantCount > 0 && state.grantCount < state.grantableCount
              }
              disabled={state.grantableCount === 0}
              onChange={(event) => state.setAllGranted(event.target.checked)}
            >
              <SyncBulkLabel>
                Grant access to{' '}
                <SyncBulkCount>{state.grantCount}</SyncBulkCount> of{' '}
                {state.grantableCount}{' '}
                {state.grantableCount === 1 ? 'address' : 'addresses'}
              </SyncBulkLabel>
            </Checkbox>
          ) : null}
        </SyncBulkBar>

        <SyncModalScroll>
          {state.rows.map((row) => {
            const id = row.item.clientBillingProfileId
            return (
              <SyncStepGroup key={id}>
                <SyncStepGroupTitle>
                  <Checkbox
                    checked={row.included}
                    onChange={(event) =>
                      state.setIncluded(id, event.target.checked)
                    }
                  >
                    {row.item.companyName}
                  </Checkbox>
                </SyncStepGroupTitle>

                {row.addedEmails.length > 0 ? (
                  <SyncField>
                    <SyncFieldLabel>Recipients to add</SyncFieldLabel>
                    <HubspotSyncEmailList
                      emails={row.addedEmails}
                      canGrant={state.canGrant && row.included}
                      isGranted={(email) => state.isGranted(id, email)}
                      onGrantedChange={(email, granted) =>
                        state.setGranted(id, email, granted)
                      }
                    />
                  </SyncField>
                ) : null}

                {row.paymentTypeNote ? (
                  <SyncFieldHint>
                    Payment type: {row.paymentTypeNote}
                  </SyncFieldHint>
                ) : null}

                {row.planStartNote ? (
                  <SyncFieldHint>Plan start: {row.planStartNote}</SyncFieldHint>
                ) : null}

                {row.submitError ? (
                  <SyncSubmitError role="alert">
                    Not updated: {row.submitError}
                  </SyncSubmitError>
                ) : null}
              </SyncStepGroup>
            )
          })}
        </SyncModalScroll>
      </>
    )}

    <SyncModalFooter>
      <SyncFooterSummary>
        {state.includedCount === 0
          ? 'No companies selected.'
          : `${state.includedCount} ${state.includedCount === 1 ? 'company' : 'companies'} selected.`}
        {state.canGrant && state.grantCount > 0
          ? ` ${state.grantCount} ${state.grantCount === 1 ? 'address' : 'addresses'} will be granted access.`
          : ''}
      </SyncFooterSummary>
      <Space>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={state.handleSubmit}
          disabled={state.includedCount === 0 || state.isBlocked}
          loading={state.isSubmitting}
        >
          Apply
        </Button>
      </Space>
    </SyncModalFooter>
  </Modal>
)

export { HubspotSyncChangesModal }
